import { Bell, Building2, DollarSign, Languages, Percent, Save } from 'lucide-react';
import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import Sidebar from '../components/layout/Sidebar';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.xl};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
    margin-bottom: ${theme.spacing.sm};
  }
  
  p {
    color: ${theme.colors.textSecondary};
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const SettingsCard = styled(Card)`
  padding: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${theme.colors.primary};
  }
`;

const SettingsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  label {
    font-size: ${theme.typography.fontSize.base};
    cursor: pointer;
  }
`;

const SaveButton = styled(Button)`
  margin-top: ${theme.spacing.lg};
  align-self: flex-start;
`;

// Initial settings data
const initialSettings = {
  restaurant: {
    name: 'Restaurant Pro',
    address: '123 Main Street, City',
    phone: '+91 9876543210',
    email: 'info@restaurant.com',
    gstNumber: 'GST123456789',
  },
  tax: {
    gstRate: 5,
    serviceCharge: 10,
    applyServiceCharge: true,
    taxTypes: [
      { name: 'GST', rate: 5, type: 'percentage', applicable: true },
      { name: 'VAT', rate: 5, type: 'percentage', applicable: false },
      { name: 'Service Tax', rate: 10, type: 'percentage', applicable: true },
    ],
  },
  currency: {
    primary: 'INR',
    symbol: '₹',
    position: 'before',
    decimalPlaces: 2,
  },
  language: {
    default: 'en',
    available: ['en', 'hi', 'es', 'fr', 'de'],
  },
  notifications: {
    emailNotifications: true,
    smsNotifications: false,
    orderAlerts: true,
    lowStockAlerts: true,
  },
};

function Settings() {
  const [settings, setSettings] = useState(initialSettings);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (section, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleSubmit = (e, section) => {
    e.preventDefault();
    // In a real app, this would save to backend
    alert(`${section} settings saved successfully!`);
    setHasChanges(false);
  };

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Settings</h1>
          <p>Manage your restaurant settings and preferences</p>
        </Header>

        <SettingsGrid>
          <SettingsCard>
            <CardHeader>
              <Building2 />
              <h2>Restaurant Information</h2>
            </CardHeader>
            <SettingsForm onSubmit={(e) => handleSubmit(e, 'Restaurant')}>
              <InputGroup>
                <label>Restaurant Name</label>
                <Input
                  type="text"
                  value={settings.restaurant.name}
                  onChange={(e) => handleChange('restaurant', 'name', e.target.value)}
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Address</label>
                <Input
                  type="text"
                  value={settings.restaurant.address}
                  onChange={(e) => handleChange('restaurant', 'address', e.target.value)}
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Phone</label>
                <Input
                  type="tel"
                  value={settings.restaurant.phone}
                  onChange={(e) => handleChange('restaurant', 'phone', e.target.value)}
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Email</label>
                <Input
                  type="email"
                  value={settings.restaurant.email}
                  onChange={(e) => handleChange('restaurant', 'email', e.target.value)}
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>GST Number</label>
                <Input
                  type="text"
                  value={settings.restaurant.gstNumber}
                  onChange={(e) => handleChange('restaurant', 'gstNumber', e.target.value)}
                  required
                />
              </InputGroup>
              <SaveButton variant="primary" type="submit" disabled={!hasChanges}>
                <Save size={20} />
                Save Restaurant Info
              </SaveButton>
            </SettingsForm>
          </SettingsCard>

          <SettingsCard>
            <CardHeader>
              <Percent />
              <h2>Tax & Charges</h2>
            </CardHeader>
            <SettingsForm onSubmit={(e) => handleSubmit(e, 'Tax')}>
              <InputGroup>
                <label>GST Rate (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.tax.gstRate}
                  onChange={(e) => handleChange('tax', 'gstRate', parseFloat(e.target.value))}
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Service Charge (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.tax.serviceCharge}
                  onChange={(e) => handleChange('tax', 'serviceCharge', parseFloat(e.target.value))}
                  required
                />
              </InputGroup>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="applyServiceCharge"
                  checked={settings.tax.applyServiceCharge}
                  onChange={(e) => handleChange('tax', 'applyServiceCharge', e.target.checked)}
                />
                <label htmlFor="applyServiceCharge">Apply Service Charge Automatically</label>
              </CheckboxGroup>
              <div style={{ marginTop: theme.spacing.lg }}>
                <h3 style={{ marginBottom: theme.spacing.md, fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semiBold }}>
                  Multiple Tax Types
                </h3>
                {settings.tax.taxTypes.map((tax, index) => (
                  <div key={index} style={{ 
                    padding: theme.spacing.md, 
                    background: theme.colors.secondary, 
                    borderRadius: theme.borderRadius.md, 
                    marginBottom: theme.spacing.sm,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <strong>{tax.name}</strong> - {tax.rate}% ({tax.type})
                    </div>
                    <CheckboxGroup>
                      <input
                        type="checkbox"
                        checked={tax.applicable}
                        onChange={(e) => {
                          const newTaxTypes = [...settings.tax.taxTypes];
                          newTaxTypes[index].applicable = e.target.checked;
                          handleChange('tax', 'taxTypes', newTaxTypes);
                        }}
                      />
                      <label>Applicable</label>
                    </CheckboxGroup>
                  </div>
                ))}
              </div>
              <SaveButton variant="primary" type="submit" disabled={!hasChanges}>
                <Save size={20} />
                Save Tax Settings
              </SaveButton>
            </SettingsForm>
          </SettingsCard>

          <SettingsCard>
            <CardHeader>
              <DollarSign />
              <h2>Currency Settings</h2>
            </CardHeader>
            <SettingsForm onSubmit={(e) => handleSubmit(e, 'Currency')}>
              <InputGroup>
                <label>Primary Currency</label>
                <select
                  value={settings.currency.primary}
                  onChange={(e) => handleChange('currency', 'primary', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    fontSize: theme.typography.fontSize.base,
                    fontFamily: theme.typography.fontFamily,
                    border: `2px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.md,
                    backgroundColor: theme.colors.white,
                  }}
                >
                  <option value="INR">Indian Rupee (₹)</option>
                  <option value="USD">US Dollar ($)</option>
                  <option value="EUR">Euro (€)</option>
                  <option value="GBP">British Pound (£)</option>
                  <option value="AED">UAE Dirham (د.إ)</option>
                </select>
              </InputGroup>
              <InputGroup>
                <label>Currency Symbol</label>
                <Input
                  type="text"
                  value={settings.currency.symbol}
                  onChange={(e) => handleChange('currency', 'symbol', e.target.value)}
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Symbol Position</label>
                <select
                  value={settings.currency.position}
                  onChange={(e) => handleChange('currency', 'position', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    fontSize: theme.typography.fontSize.base,
                    fontFamily: theme.typography.fontFamily,
                    border: `2px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.md,
                    backgroundColor: theme.colors.white,
                  }}
                >
                  <option value="before">Before Amount (₹100)</option>
                  <option value="after">After Amount (100₹)</option>
                </select>
              </InputGroup>
              <InputGroup>
                <label>Decimal Places</label>
                <Input
                  type="number"
                  min="0"
                  max="4"
                  value={settings.currency.decimalPlaces}
                  onChange={(e) => handleChange('currency', 'decimalPlaces', parseInt(e.target.value))}
                  required
                />
              </InputGroup>
              <SaveButton variant="primary" type="submit" disabled={!hasChanges}>
                <Save size={20} />
                Save Currency Settings
              </SaveButton>
            </SettingsForm>
          </SettingsCard>

          <SettingsCard>
            <CardHeader>
              <Languages />
              <h2>Language Settings</h2>
            </CardHeader>
            <SettingsForm onSubmit={(e) => handleSubmit(e, 'Language')}>
              <InputGroup>
                <label>Default Language</label>
                <select
                  value={settings.language.default}
                  onChange={(e) => handleChange('language', 'default', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    fontSize: theme.typography.fontSize.base,
                    fontFamily: theme.typography.fontFamily,
                    border: `2px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.md,
                    backgroundColor: theme.colors.white,
                  }}
                >
                  <option value="en">English</option>
                  <option value="hi">हिंदी (Hindi)</option>
                  <option value="es">Español (Spanish)</option>
                  <option value="fr">Français (French)</option>
                  <option value="de">Deutsch (German)</option>
                </select>
              </InputGroup>
              <div style={{ marginTop: theme.spacing.md }}>
                <label style={{ display: 'block', marginBottom: theme.spacing.sm, fontWeight: theme.typography.fontWeight.semiBold }}>
                  Available Languages
                </label>
                {settings.language.available.map((lang) => {
                  const langNames = {
                    en: 'English',
                    hi: 'हिंदी',
                    es: 'Español',
                    fr: 'Français',
                    de: 'Deutsch',
                  };
                  return (
                    <CheckboxGroup key={lang} style={{ marginBottom: theme.spacing.xs }}>
                      <input
                        type="checkbox"
                        checked={true}
                        readOnly
                      />
                      <label>{langNames[lang] || lang}</label>
                    </CheckboxGroup>
                  );
                })}
              </div>
              <SaveButton variant="primary" type="submit" disabled={!hasChanges}>
                <Save size={20} />
                Save Language Settings
              </SaveButton>
            </SettingsForm>
          </SettingsCard>

          <SettingsCard>
            <CardHeader>
              <Bell />
              <h2>Notifications</h2>
            </CardHeader>
            <SettingsForm onSubmit={(e) => handleSubmit(e, 'Notifications')}>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="emailNotifications"
                  checked={settings.notifications.emailNotifications}
                  onChange={(e) => handleChange('notifications', 'emailNotifications', e.target.checked)}
                />
                <label htmlFor="emailNotifications">Email Notifications</label>
              </CheckboxGroup>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="smsNotifications"
                  checked={settings.notifications.smsNotifications}
                  onChange={(e) => handleChange('notifications', 'smsNotifications', e.target.checked)}
                />
                <label htmlFor="smsNotifications">SMS Notifications</label>
              </CheckboxGroup>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="orderAlerts"
                  checked={settings.notifications.orderAlerts}
                  onChange={(e) => handleChange('notifications', 'orderAlerts', e.target.checked)}
                />
                <label htmlFor="orderAlerts">Order Alerts</label>
              </CheckboxGroup>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="lowStockAlerts"
                  checked={settings.notifications.lowStockAlerts}
                  onChange={(e) => handleChange('notifications', 'lowStockAlerts', e.target.checked)}
                />
                <label htmlFor="lowStockAlerts">Low Stock Alerts</label>
              </CheckboxGroup>
              <SaveButton variant="primary" type="submit" disabled={!hasChanges}>
                <Save size={20} />
                Save Notification Settings
              </SaveButton>
            </SettingsForm>
          </SettingsCard>
        </SettingsGrid>
      </MainContent>
      <Footer />
    </Container>
  );
}

export default Settings;

